// Deterministic financial engines for the tools platform.
// Pure functions, no LLM at calculation time (binding spec §7).
// Every function handles zero/negative/extreme inputs explicitly.

const num = (v, d = 0) => {
  const n = typeof v === "string" ? parseFloat(v.replace(/[₹,\s]/g, "")) : Number(v);
  return Number.isFinite(n) ? n : d;
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** INR display: 12,50,000 / ₹1.25 Cr / ₹4.2 L */
export function inr(n, { symbol = true } = {}) {
  const abs = Math.abs(n);
  const s = symbol ? "₹" : "";
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}${s}${round2(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}${s}${round2(abs / 1e5)} L`;
  return `${sign}${s}${Math.round(abs).toLocaleString("en-IN")}`;
}
export const pct = (n, digits = 1) => `${round2(n * 100).toFixed(digits).replace(/\.0+$/, "")}%`;

/**
 * P03 Discount Damage. Reference: price 1000, cost 700, 10% off →
 * new contribution 200, volume must rise 1.5x (+50%) to preserve total contribution.
 * If post-discount contribution <= 0, no finite volume preserves contribution → null.
 */
export function discountDamage({ price, variableCost, discountPct, quantity = 100 }) {
  const p = num(price), c = num(variableCost), q = Math.max(0, num(quantity, 100));
  const d = clamp(num(discountPct), 0, 100) / 100;
  const before = p - c;
  const newPrice = p * (1 - d);
  const after = newPrice - c;
  const totalBefore = before * q;
  const totalAfterAtSameVolume = after * q;
  let requiredMultiplier = null;
  if (after > 0 && before > 0) requiredMultiplier = before / after;
  return {
    priceNew: round2(newPrice),
    contributionBefore: round2(before),
    contributionAfter: round2(after),
    contributionPctBefore: p > 0 ? before / p : 0,
    contributionPctAfter: newPrice > 0 ? after / newPrice : 0,
    totalBefore: round2(totalBefore),
    totalAfterSameVolume: round2(totalAfterAtSameVolume),
    requiredMultiplier: requiredMultiplier === null ? null : round2(requiredMultiplier),
    extraUnitsNeededPct: requiredMultiplier === null ? null : round2((requiredMultiplier - 1) * 100),
    impossible: after <= 0 && before > 0,
    curve: Array.from({ length: 13 }, (_, i) => i * 2.5).map((dp) => {
      const np = p * (1 - dp / 100);
      const ca = np - c;
      return { discount: dp, contribution: round2(ca), required: ca > 0 && before > 0 ? round2(before / ca) : null };
    }),
  };
}

/** P04 Price increase trade-off. Break-even tolerable volume loss fraction. */
export function priceIncrease({ price, variableCost, units = 100, increasePct }) {
  const p = num(price), c = num(variableCost), u = Math.max(0, num(units, 100));
  const inc = num(increasePct) / 100;
  const np = p * (1 + inc);
  const before = p - c, after = np - c;
  const totalBefore = before * u;
  const totalAfter = after * u; // at same volume
  // break-even volume: after * uNew = totalBefore → uNew = totalBefore / after
  const breakEvenUnits = after > 0 ? totalBefore / after : null;
  const tolerableLossPct = breakEvenUnits === null || u === 0 ? null : Math.max(0, (1 - breakEvenUnits / u) * 100);
  return {
    newPrice: round2(np), contributionBefore: round2(before), contributionAfter: round2(after),
    totalBefore: round2(totalBefore), totalAfterSameVolume: round2(totalAfter),
    breakEvenUnits: breakEvenUnits === null ? null : round2(breakEvenUnits),
    tolerableLossPct: tolerableLossPct === null ? null : round2(tolerableLossPct),
    sensitivity: [0.5, 1, 2].map((elasticity) => {
      const volumeChange = -elasticity * inc * 100; // % per % price
      const uNew = Math.max(0, u * (1 + volumeChange / 100));
      return { elasticity, volumeChangePct: round2(volumeChange), total: round2(after * uNew), vsBefore: round2(after * uNew - totalBefore) };
    }),
  };
}

/** P09 Break-even. Single product or weighted mix. */
export function breakEven({ fixedCosts, price, variableCost, currentUnits = 0, mix = null }) {
  const fc = num(fixedCosts);
  let cmPerUnit, cmRatio;
  if (mix && mix.length) {
    // mix: [{share (0-1), contributionPct}]
    cmRatio = mix.reduce((s, m) => s + clamp(num(m.share), 0, 1) * num(m.contributionPct) / 100, 0);
    cmPerUnit = null;
  } else {
    const p = num(price), c = num(variableCost);
    cmPerUnit = p - c;
    cmRatio = p > 0 ? cmPerUnit / p : 0;
  }
  const beRevenue = cmRatio > 0 ? fc / cmRatio : null;
  const beUnits = cmPerUnit && cmPerUnit > 0 ? fc / cmPerUnit : null;
  const curUnits = Math.max(0, num(currentUnits));
  const currentRevenue = cmPerUnit ? curUnits * num(price) : 0;
  const marginOfSafetyPct = beRevenue && currentRevenue > 0 ? Math.max(0, (1 - beRevenue / currentRevenue) * 100) : null;
  return {
    contributionRatio: round2(cmRatio * 100),
    breakEvenRevenue: beRevenue === null ? null : round2(beRevenue),
    breakEvenUnits: beUnits === null ? null : round2(beUnits),
    marginOfSafetyPct: marginOfSafetyPct === null ? null : round2(marginOfSafetyPct),
    warning: cmRatio <= 0 ? "Contribution is zero or negative — no sales level breaks even." : null,
  };
}

/** D02 Amazon break-even ACOS. ACOS base = ad spend / ad-attributed sales. */
export function amazonAcos({ price, referralPct = 0, closingFee = 0, fulfilmentFee = 0, productCost = 0, packingCost = 0, otherVariable = 0, promoCost = 0, targetContributionPct = 0 }) {
  const p = num(price);
  const fees = p * num(referralPct) / 100 + num(closingFee) + num(fulfilmentFee);
  const costs = num(productCost) + num(packingCost) + num(otherVariable) + num(promoCost);
  const contributionBeforeAds = p - fees - costs;
  const breakEvenAcos = p > 0 ? Math.max(0, contributionBeforeAds / p) : 0;
  const targetAcos = p > 0 ? Math.max(0, (contributionBeforeAds - p * num(targetContributionPct) / 100) / p) : 0;
  return {
    fees: round2(fees), costs: round2(costs),
    contributionBeforeAds: round2(contributionBeforeAds),
    contributionPct: p > 0 ? round2(contributionBeforeAds / p * 100) : 0,
    breakEvenAcosPct: round2(breakEvenAcos * 100),
    targetAcosPct: round2(targetAcos * 100),
    negative: contributionBeforeAds <= 0,
  };
}

/** D03 Break-even ROAS on contribution (accounts for returns). */
export function breakEvenRoas({ contributionPct, returnRatePct = 0, targetContributionPct = 0 }) {
  const cm = clamp(num(contributionPct), 0, 100) / 100;
  const rr = clamp(num(returnRatePct), 0, 100) / 100;
  const tgt = clamp(num(targetContributionPct), 0, 100) / 100;
  // effective contribution per ₹1 of gross ad-attributed revenue
  const effAfterReturns = cm * (1 - rr);
  // break-even: ad spend may consume the entire effective contribution
  const be = effAfterReturns > 0 ? 1 / effAfterReturns : null;
  // target: ad spend must leave tgt contribution behind
  const eff = effAfterReturns - tgt;
  const target = eff > 0 ? 1 / eff : null;
  const beGross = cm > 0 ? 1 / cm : null;
  return {
    effectiveContributionPct: round2(effAfterReturns * 100),
    breakEvenRoas: be === null ? null : round2(be),
    breakEvenRoasNoReturns: beGross === null ? null : round2(beGross),
    targetRoas: target === null ? null : round2(target),
    impossible: eff <= 0,
  };
}

/** D01 E-commerce per-order economics waterfall. */
export function unitEconomics({ price, gstInclusive = true, gstRatePct = 18, discountPct = 0, productCost = 0, platformFeePct = 0, paymentFeePct = 0, packaging = 0, shipping = 0, returnRatePct = 0, reverseShipping = 0, adCostPerOrder = 0 }) {
  const gross = num(price);
  const discounted = gross * (1 - clamp(num(discountPct), 0, 100) / 100);
  const netRevenue = gstInclusive ? discounted / (1 + num(gstRatePct) / 100) : discounted; // exclude GST
  const gstAmount = discounted - netRevenue;
  const platformFee = discounted * num(platformFeePct) / 100;
  const paymentFee = discounted * num(paymentFeePct) / 100;
  const rr = clamp(num(returnRatePct), 0, 100) / 100;
  const expectedReturnCost = rr * (num(reverseShipping) + num(productCost) * 0.5); // 50% goods recovery assumption, disclosed
  const successContribution = netRevenue - num(productCost) - platformFee - paymentFee - num(packaging) - num(shipping);
  const perPlacedOrder = successContribution - expectedReturnCost - num(adCostPerOrder);
  return {
    grossPrice: round2(gross), discounted: round2(discounted), gstAmount: round2(gstAmount), netRevenue: round2(netRevenue),
    platformFee: round2(platformFee), paymentFee: round2(paymentFee),
    expectedReturnCost: round2(expectedReturnCost),
    successContribution: round2(successContribution),
    perPlacedOrderContribution: round2(perPlacedOrder),
    breakEvenAdCost: round2(Math.max(0, successContribution - expectedReturnCost)),
    waterfall: [
      { label: "Selling price", value: round2(gross) },
      { label: "Discount", value: round2(discounted - gross) },
      { label: "GST", value: round2(-gstAmount) },
      { label: "Product cost", value: round2(-num(productCost)) },
      { label: "Platform fee", value: round2(-platformFee) },
      { label: "Payment fee", value: round2(-paymentFee) },
      { label: "Packaging", value: round2(-num(packaging)) },
      { label: "Shipping", value: round2(-num(shipping)) },
      { label: "Returns (expected)", value: round2(-expectedReturnCost) },
      { label: "Ads per order", value: round2(-num(adCostPerOrder)) },
      { label: "Contribution / order", value: round2(perPlacedOrder), total: true },
    ],
  };
}

/** S01 Sales force productivity. */
export function salesProductivity({ reps, sellingDaysPerMonth = 22, hoursPerDay = 8, travelAdminPct = 45, visitsPerDay = 6, conversionPct = 20, avgOrderValue = 25000, monthlyRevenue = 0 }) {
  const r = Math.max(1, num(reps, 1));
  const sellingHours = num(sellingDaysPerMonth, 22) * num(hoursPerDay, 8) * (1 - clamp(num(travelAdminPct), 0, 95) / 100);
  const visits = num(sellingDaysPerMonth, 22) * num(visitsPerDay);
  const orders = visits * clamp(num(conversionPct), 0, 100) / 100;
  const revenue = orders * num(avgOrderValue);
  const revenuePerRep = revenue; // per rep per month
  const reported = num(monthlyRevenue);
  return {
    sellingHoursPerRepMonth: round2(sellingHours),
    visitsPerRepMonth: round2(visits),
    ordersPerRepMonth: round2(orders),
    modelledRevenuePerRep: round2(revenuePerRep),
    teamRevenue: round2(revenuePerRep * r),
    actualRevenuePerRep: reported > 0 ? round2(reported / r) : null,
    scenarios: [10, 20, 30].map((uplift) => ({
      upliftPct: uplift,
      revenuePerRep: round2(revenuePerRep * (1 + uplift / 100)),
      teamRevenue: round2(revenuePerRep * (1 + uplift / 100) * r),
    })),
  };
}

/** M13 sample size: SRS with finite population correction. */
export function sampleSize({ confidencePct = 95, marginPct = 5, proportionPct = 50, population = 0 }) {
  const Z = { 80: 1.282, 85: 1.44, 90: 1.645, 95: 1.96, 99: 2.576 }[num(confidencePct, 95)] || 1.96;
  const e = clamp(num(marginPct, 5), 0.5, 50) / 100;
  const p = clamp(num(proportionPct, 50), 1, 99) / 100;
  const n0 = (Z * Z * p * (1 - p)) / (e * e);
  const N = num(population);
  const n = N > 0 ? n0 / (1 + (n0 - 1) / N) : n0;
  return { z: Z, nRaw: Math.ceil(n0), n: Math.ceil(n), finiteCorrection: N > 0 };
}

/** G12 HHI concentration (0–10000) from shares. */
export function hhi(shares) {
  const total = shares.reduce((s, v) => s + Math.max(0, num(v)), 0);
  if (total <= 0) return { hhi: 0, topShare: 0 };
  const fracs = shares.map((v) => Math.max(0, num(v)) / total);
  return {
    hhi: Math.round(fracs.reduce((s, f) => s + f * f, 0) * 10000),
    topShare: round2(Math.max(...fracs) * 100),
  };
}

/** D05 CAC vs LTV (contribution-based, discounted). */
export function cacLtv({ cac, firstContribution, repeatContribution = 0, retentionPct = 60, periods = 12, discountRatePct = 12 }) {
  const c = num(cac);
  const r = clamp(num(retentionPct), 0, 100) / 100;
  const dRate = num(discountRatePct, 12) / 100 / 12; // monthly
  let ltv = num(firstContribution);
  let retained = 1;
  for (let t = 1; t <= Math.min(120, num(periods, 12)); t++) {
    retained *= r;
    ltv += (num(repeatContribution) * retained) / Math.pow(1 + dRate, t);
  }
  // payback: first month in which cumulative RETAINED contribution covers CAC;
  // null when the horizon never recovers it (e.g. LTV < CAC)
  let paybackPeriods = null;
  if (c > 0 && (num(firstContribution) > 0 || num(repeatContribution) > 0)) {
    let cum = num(firstContribution);
    if (cum >= c) {
      paybackPeriods = 1;
    } else {
      let retainedCum = 1;
      for (let t = 1; t <= Math.min(120, num(periods, 12)); t++) {
        retainedCum *= r;
        cum += num(repeatContribution) * retainedCum;
        if (cum >= c) { paybackPeriods = t + 1; break; }
      }
    }
  }
  return {
    ltv: round2(ltv),
    ltvCac: c > 0 ? round2(ltv / c) : null,
    paybackMonths: paybackPeriods,
    maxCacForRatio3: round2(ltv / 3),
  };
}

export function inr2(n) {
  // unit-level money: keep paise under ₹1,000 so small per-unit values don't collapse to ₹0
  const v = round2(num(n));
  return Math.abs(v) < 1000 ? `₹${v.toFixed(2)}` : inr(v);
}

export const __testables = { num, clamp, round2 };

/** G03 Hidden revenue waterfall. levers: [{label, eligible, adoptionPct, valuePerAdopter}].
 * Overlap: a customer-based lever stacked after others is discounted by overlapPct
 * applied sequentially (each subsequent lever's eligible pool shrinks). */
export function hiddenRevenue({ levers, overlapPct = 15, scenario = "base" }) {
  const scale = { conservative: 0.5, base: 1, stretch: 1.5 }[scenario] || 1;
  const overlap = clamp(num(overlapPct, 15), 0, 90) / 100;
  let poolFactor = 1;
  const rows = levers.map((l) => {
    const eligible = Math.max(0, num(l.eligible));
    const adoption = clamp(num(l.adoptionPct) * scale, 0, 100) / 100;
    const value = Math.max(0, num(l.valuePerAdopter));
    const effectiveEligible = eligible * poolFactor;
    const gross = effectiveEligible * adoption * value;
    poolFactor *= 1 - overlap; // next lever's pool shrinks by overlap
    return { label: l.label, eligible, adoptionPct: round2(adoption * 100), value: round2(gross) };
  });
  const total = rows.reduce((s, r) => s + r.value, 0);
  return { rows, total: round2(total), overlapPct: round2(overlap * 100), scenario };
}

/** G04 Revenue bridge. drivers: [{label, mode:'add'|'pct', value}].
 * 'add' = absolute ₹; 'pct' = % of the RUNNING total (applied sequentially).
 * Double-count guard: each driver applies once; baseline uplift is its own driver. */
export function revenueBridge({ baseline, target, drivers }) {
  const base = Math.max(0, num(baseline));
  const tgt = Math.max(0, num(target));
  let running = base;
  const steps = drivers.map((d) => {
    const amt = d.mode === "pct" ? running * (num(d.value) / 100) : num(d.value);
    const from = running;
    running += amt;
    return { label: d.label, from: round2(from), delta: round2(amt), to: round2(running) };
  });
  const gap = tgt - running;
  return { baseline: base, target: tgt, steps, projected: round2(running), gap: round2(gap), gapClosedPct: tgt > base ? round2(clamp((running - base) / (tgt - base), 0, 9) * 100) : null };
}

/** P02 Price waterfall. steps: [{label, pct, base:'list'|'running'}]. */
export function priceWaterfall({ listPrice, steps }) {
  const list = Math.max(0, num(listPrice));
  let running = list;
  const rows = steps.map((s) => {
    const p = clamp(num(s.pct), -100, 100) / 100;
    const base = s.base === "list" ? list : running;
    const deduction = base * p;
    running -= deduction;
    return { label: s.label, pct: num(s.pct), base: s.base, deduction: round2(deduction), after: round2(running) };
  });
  return {
    list: round2(list), pocket: round2(running),
    realizationPct: list > 0 ? round2((running / list) * 100) : 0,
    totalLeakage: round2(list - running),
    rows,
  };
}

/** P08 Trade promotion ROI. */
export function tradePromoRoi({ baselineUnits, promotedUnits, regularPrice, promoPrice, variableCost, schemeCost, cannibalPct = 15, pullForwardPct = 10 }) {
  const bu = Math.max(0, num(baselineUnits)), pu = Math.max(0, num(promotedUnits));
  const rp = num(regularPrice), pp = num(promoPrice), c = num(variableCost);
  const cost = Math.max(0, num(schemeCost));
  const observed = pu - bu;
  const cannibal = pu * clamp(num(cannibalPct, 15), 0, 100) / 100;
  const pullFwd = Math.max(0, observed - cannibal) * clamp(num(pullForwardPct, 10), 0, 100) / 100;
  const incremental = Math.max(0, observed - cannibal - pullFwd);
  const incrContribution = incremental * (pp - c);
  const roi = cost > 0 ? (incrContribution - cost) / cost : null;
  const breakEvenUplift = pp > c ? cost / (pp - c) : null;
  return {
    observedUpliftUnits: round2(observed),
    cannibalizedUnits: round2(cannibal),
    pullForwardUnits: round2(pullFwd),
    incrementalUnits: round2(incremental),
    incrementalContribution: round2(incrContribution),
    schemeCost: round2(cost),
    roiPct: roi === null ? null : round2(roi * 100),
    breakEvenUpliftUnits: breakEvenUplift === null ? null : Math.ceil(breakEvenUplift),
  };
}

/** P12 B2B quotation profitability. */
export function quotation({ quantity, pricePerUnit, materialPerUnit, laborPerUnit, overheadPct = 15, tooling = 0, packing = 0, shipping = 0, creditDays = 30, financeRatePct = 12 }) {
  const q = Math.max(0, num(quantity, 1));
  const price = num(pricePerUnit);
  const direct = (num(materialPerUnit) + num(laborPerUnit)) * q;
  const overhead = direct * clamp(num(overheadPct, 15), 0, 200) / 100;
  const other = num(tooling) + num(packing) + num(shipping);
  const revenue = price * q;
  const financeCost = revenue * (num(financeRatePct, 12) / 100) * (clamp(num(creditDays, 30), 0, 365) / 365);
  const totalCost = direct + overhead + other + financeCost;
  const contribution = revenue - totalCost;
  const floorPerUnit = q > 0 ? totalCost / q : 0;
  return {
    revenue: round2(revenue), directCost: round2(direct), overhead: round2(overhead),
    otherCosts: round2(other), financeCost: round2(financeCost), totalCost: round2(totalCost),
    contribution: round2(contribution),
    contributionPct: revenue > 0 ? round2((contribution / revenue) * 100) : 0,
    floorPricePerUnit: round2(floorPerUnit),
    profitable: contribution > 0,
  };
}

/** P13 Working capital. DSO on sales, DIO/DPO on COGS (per spec). */
export function workingCapital({ annualSales, annualCogs, receivables, inventory, payables, days = 365 }) {
  const d = num(days, 365);
  const sales = Math.max(0, num(annualSales)), cogs = Math.max(0, num(annualCogs));
  const dso = sales > 0 ? (num(receivables) / sales) * d : 0;
  const dio = cogs > 0 ? (num(inventory) / cogs) * d : 0;
  const dpo = cogs > 0 ? (num(payables) / cogs) * d : 0;
  const ccc = dso + dio - dpo;
  const perDaySales = sales / d;
  return {
    dso: round2(dso), dio: round2(dio), dpo: round2(dpo), ccc: round2(ccc),
    releasePerDayDso: round2(perDaySales),
    release5Days: round2(perDaySales * 5),
  };
}

/** P10 Customer profitability. rows: [{id, revenue, productCost, discounts, freight, service, credit}]. */
export function customerProfitability(rows) {
  const out = rows.map((r) => {
    const revenue = Math.max(0, num(r.revenue));
    const costs = num(r.productCost) + num(r.discounts) + num(r.freight) + num(r.service) + num(r.credit);
    const contribution = revenue - costs;
    return { id: r.id || "?", revenue: round2(revenue), contribution: round2(contribution), marginPct: revenue > 0 ? round2((contribution / revenue) * 100) : 0 };
  });
  out.sort((a, b) => b.contribution - a.contribution);
  const totalRev = out.reduce((s, r) => s + r.revenue, 0);
  const totalContrib = out.reduce((s, r) => s + r.contribution, 0);
  const medianRev = out.length ? out[Math.floor(out.length / 2)].revenue : 0;
  return {
    rows: out.map((r) => ({
      ...r,
      quadrant: r.revenue >= medianRev && r.marginPct >= 15 ? "grow" : r.revenue >= medianRev ? "reprice" : r.marginPct >= 15 ? "service-efficiently" : "review",
    })),
    totalRevenue: round2(totalRev),
    totalContribution: round2(totalContrib),
    avgMarginPct: totalRev > 0 ? round2((totalContrib / totalRev) * 100) : 0,
  };
}

/** P11 Portfolio X-Ray. rows: [{sku, revenue, contributionPct, growthPct, turns, strategic}]. */
export function portfolioXray(rows) {
  const valid = rows.map((r) => ({
    sku: r.sku || "?", revenue: Math.max(0, num(r.revenue)),
    contributionPct: clamp(num(r.contributionPct), -100, 100),
    growthPct: num(r.growthPct), turns: Math.max(0, num(r.turns)),
    strategic: !!r.strategic,
  }));
  const totalRev = valid.reduce((s, r) => s + r.revenue, 0);
  const avgContribution = totalRev > 0 ? valid.reduce((s, r) => s + r.revenue * r.contributionPct, 0) / totalRev : 0;
  const shareCutoff = valid.length ? Math.max(0.02, 0.8 / valid.length) : 0; // meaningful-share proxy
  const groups = { hero: [], maintain: [], fix: [], investigate: [], "retire-review": [] };
  for (const r of valid) {
    const share = totalRev > 0 ? r.revenue / totalRev : 0;
    let g;
    if (r.strategic) g = "investigate"; // strategic-role SKUs need rationale, not auto-kill
    else if (share >= shareCutoff && r.contributionPct >= avgContribution) g = "hero";
    else if (share >= shareCutoff && r.contributionPct < avgContribution) g = "fix";
    else if (share < shareCutoff && r.contributionPct >= avgContribution) g = "maintain";
    else g = "retire-review";
    groups[g].push({ ...r, sharePct: round2(share * 100) });
  }
  return { groups, totalRevenue: round2(totalRev), avgContributionPct: round2(avgContribution), skuCount: valid.length };
}

/** P14 Competitor price ladder. rows: [{competitor, price, packSize, date}]. */
export function priceLadder(rows) {
  const valid = rows
    .map((r) => ({ ...r, price: Math.max(0, num(r.price)), packSize: Math.max(0, num(r.packSize, 1)) }))
    .filter((r) => r.price > 0)
    .map((r) => ({ ...r, perUnit: round2(r.price / r.packSize) }))
    .sort((a, b) => a.perUnit - b.perUnit);
  const gaps = [];
  for (let i = 1; i < valid.length; i++) {
    const gap = valid[i].perUnit - valid[i - 1].perUnit;
    const midPct = valid[i - 1].perUnit > 0 ? gap / valid[i - 1].perUnit : 0;
    if (midPct > 0.15) gaps.push({ between: `${valid[i - 1].competitor} → ${valid[i].competitor}`, gapPerUnit: round2(gap), gapPct: round2(midPct * 100) });
  }
  return { ladder: valid, gaps, cheapest: valid[0] || null, priciest: valid[valid.length - 1] || null };
}

/** G12 concentration with loss scenarios. */
export function concentration(items) {
  const vals = items.map((v) => Math.max(0, num(v))).filter((v) => v > 0);
  const total = vals.reduce((s, v) => s + v, 0);
  const sorted = [...vals].sort((a, b) => b - a);
  const base = hhi(vals);
  const loss = (n) => {
    const remaining = total - sorted.slice(0, n).reduce((s, v) => s + v, 0);
    return { lose: n, revenueLeft: round2(remaining), impactPct: total > 0 ? round2(((total - remaining) / total) * 100) : 0 };
  };
  return {
    ...base,
    total: round2(total),
    top1Pct: total > 0 ? round2((sorted[0] / total) * 100) : 0,
    top3Pct: total > 0 ? round2((sorted.slice(0, 3).reduce((s, v) => s + v, 0) / total) * 100) : 0,
    loseTop1: loss(1), loseTop2: loss(2),
  };
}

/** G06 weighted prioritization with normalization. */
export function prioritizeBets({ bets, weights }) {
  // bets: [{name, impact, speed, fit, investment, risk, confidence}] scored 1-5 (investment & risk: 1=high cost)
  const w = Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, clamp(num(v, 1), 0, 10)]));
  const wsum = Object.values(w).reduce((s, v) => s + v, 0) || 1;
  const scored = bets.map((b) => {
    const total = (num(b.impact) * w.impact + num(b.speed) * w.speed + num(b.fit) * w.fit + num(b.investment) * w.investment + num(b.risk) * w.risk + num(b.confidence) * w.confidence) / wsum;
    return { ...b, score: round2((total / 5) * 100) };
  }).sort((a, b) => b.score - a.score);
  return scored.map((b, i) => ({
    ...b,
    bucket: b.score >= 70 ? "now" : b.score >= 50 ? "next" : b.score >= 35 ? "later" : "no-go",
    rank: i + 1,
  }));
}

/** Generic weighted scoring matrix (M03 segment attractiveness, C03 route-to-market).
 * items: [{name, scores:{criterion:1-5}}]; criteria: [{key,label}]; weights: {criterion:0-10}. */
export function weightedMatrix({ items, criteria, weights }) {
  const w = Object.fromEntries(Object.entries(weights).map(([k, v]) => [k, clamp(num(v, 1), 0, 10)]));
  const wsum = Object.values(w).reduce((s, v) => s + v, 0) || 1;
  return items
    .map((it) => {
      const total = criteria.reduce((s, c) => s + clamp(num(it.scores?.[c.key], 0), 0, 5) * (w[c.key] || 0), 0);
      return { ...it, score: round2((total / (wsum * 5)) * 100) };
    })
    .sort((a, b) => b.score - a.score)
    .map((it, i) => ({ ...it, rank: i + 1 }));
}

/** M01 Honest TAM–SAM–SOM (bottom-up). */
export function tamSamSom({ accounts, frequencyPerYear, avgValue, geographyPct = 100, segmentFitPct = 100, capabilityPct = 100, shareScenariosPct = [1, 3, 5] }) {
  const tam = Math.max(0, num(accounts)) * Math.max(0, num(frequencyPerYear)) * Math.max(0, num(avgValue));
  const geo = clamp(num(geographyPct, 100), 0, 100) / 100;
  const seg = clamp(num(segmentFitPct, 100), 0, 100) / 100;
  const cap = clamp(num(capabilityPct, 100), 0, 100) / 100;
  const sam = tam * geo * seg * cap;
  const som = (shareScenariosPct || []).map((s) => ({ sharePct: num(s), value: round2(sam * clamp(num(s), 0, 100) / 100) }));
  const warnings = [];
  if (geo * seg * cap > 0.8) warnings.push("Your SAM filters remove less than 20% of TAM — constraint assumptions may be optimistic.");
  if (sam > 0 && som.some((s) => s.value / sam > 0.2)) warnings.push("A >20% SOM share scenario is rare in year one — treat it as a stretch case.");
  return { tam: round2(tam), sam: round2(sam), som, samPctOfTam: tam > 0 ? round2((sam / tam) * 100) : 0, warnings };
}

/** M11 Journey friction. stages: [{stage, entrants, dropPct, avgValue}]. */
export function journeyFriction(stages) {
  let remaining = stages.length ? Math.max(0, num(stages[0].entrants)) : 0;
  // probability a unit that gets PAST stage i still reaches the end
  const probBeyond = (i) => {
    let p = 1;
    for (let j = i + 1; j < stages.length; j++) p *= 1 - clamp(num(stages[j].dropPct), 0, 100) / 100;
    return p;
  };
  const rows = stages.map((s, i) => {
    const entrants = i === 0 ? remaining : remaining;
    const drop = entrants * clamp(num(s.dropPct), 0, 100) / 100;
    // value a drop-off at its real chance of having bought, not at full value —
    // otherwise the top of the funnel always looks like the biggest leak
    const leakValue = drop * probBeyond(i) * Math.max(0, num(s.avgValue));
    remaining = Math.max(0, entrants - drop);
    return { stage: s.stage, entrants: round2(entrants), dropped: round2(drop), leakValue: round2(leakValue) };
  });
  const totalLeak = rows.reduce((s, r) => s + r.leakValue, 0);
  const biggest = rows.reduce((m, r) => (r.leakValue > (m?.leakValue || 0) ? r : m), null);
  return { rows, survivors: round2(remaining), totalLeak: round2(totalLeak), biggestLeak: biggest?.stage || null };
}

/** M12 Trend readiness radar. trends: [{trend, relevance 1-5, velocity 1-5, readiness 1-5}]. */
export function trendReadiness(trends) {
  const rows = trends.map((t) => {
    const exposure = clamp(num(t.relevance), 0, 5) * clamp(num(t.velocity), 0, 5); // 0-25
    const ready = clamp(num(t.readiness), 0, 5);
    const gap = exposure - ready * 5;
    return { trend: t.trend, exposure: round2(exposure), readinessPct: round2((ready / 5) * 100), gap: round2(gap), urgent: gap >= 10 };
  }).sort((a, b) => b.gap - a.gap);
  return { rows, urgentCount: rows.filter((r) => r.urgent).length };
}

/** C04 Channel economics comparator. channels: [{name, realizationPct, cogsPct, channelMarginPct, logisticsPct, creditDays, servicePct, monthlyUnits}]. */
export function channelEconomics(channels, { mrp = 100, financeRatePct = 12 } = {}) {
  const price = Math.max(0, num(mrp, 100));
  const rows = channels.map((c) => {
    const realization = price * clamp(num(c.realizationPct, 100), 0, 200) / 100;
    const cogs = price * clamp(num(c.cogsPct), 0, 100) / 100;
    const channelCost = realization * clamp(num(c.channelMarginPct), 0, 90) / 100;
    const logistics = realization * clamp(num(c.logisticsPct), 0, 50) / 100;
    const credit = realization * (num(financeRatePct, 12) / 100) * (clamp(num(c.creditDays), 0, 365) / 365);
    const service = realization * clamp(num(c.servicePct), 0, 50) / 100;
    const contribution = realization - cogs - channelCost - logistics - credit - service;
    return {
      name: c.name, realization: round2(realization), totalCost: round2(cogs + channelCost + logistics + credit + service),
      contribution: round2(contribution), contributionPct: realization > 0 ? round2((contribution / realization) * 100) : 0,
      monthlyContribution: round2(contribution * Math.max(0, num(c.monthlyUnits))),
    };
  }).sort((a, b) => b.contributionPct - a.contributionPct);
  return { rows, best: rows[0]?.name || null };
}

/** C06 Distributor ROI (partner-side view). */
export function distributorRoi({ monthlyPurchase, marginPct, monthlyExpenses, capitalDeployed, inventoryDays = 20, marketCreditDays = 30, companyCreditDays = 0 }) {
  const purchase = Math.max(0, num(monthlyPurchase));
  const margin = purchase * clamp(num(marginPct), -20, 60) / 100;
  const expenses = Math.max(0, num(monthlyExpenses));
  const earnings = margin - expenses;
  const cycleDays = Math.max(1, num(inventoryDays, 20) + num(marketCreditDays, 30) - num(companyCreditDays));
  const rotations = 30 / cycleDays;
  // Capital actually needed if not given: purchase value locked per cycle
  const capital = num(capitalDeployed) > 0 ? num(capitalDeployed) : (purchase / 30) * cycleDays;
  const monthlyRoi = capital > 0 ? (earnings / capital) * 100 : 0;
  return {
    grossMarginMonthly: round2(margin), netEarningsMonthly: round2(earnings),
    cycleDays: round2(cycleDays), rotationsPerMonth: round2(rotations),
    capitalRequired: round2(capital), monthlyRoiPct: round2(monthlyRoi), annualRoiPct: round2(monthlyRoi * 12),
    viable: earnings > 0,
  };
}

/** C07 Dealer–SKU white space. dealers:[{name, monthlyVolume}], skus:[{name, avgPerDealer}], stocked: {dealerIdx:[skuIdx]}. */
export function dealerSkuGap({ dealers, skus, stocked }) {
  const avgVolume = dealers.length ? dealers.reduce((s, d) => s + num(d.monthlyVolume), 0) / dealers.length : 1;
  const gaps = [];
  dealers.forEach((d, di) => {
    const scale = avgVolume > 0 ? num(d.monthlyVolume) / avgVolume : 1;
    skus.forEach((s, si) => {
      if (!stocked?.[di]?.includes(si)) {
        gaps.push({ dealer: d.name, sku: s.name, opportunity: round2(num(s.avgPerDealer) * scale) });
      }
    });
  });
  gaps.sort((a, b) => b.opportunity - a.opportunity);
  const byDealer = dealers.map((d) => ({
    name: d.name,
    missing: skus.length - (stocked?.[dealers.indexOf(d)]?.length || 0),
    opportunity: round2(gaps.filter((g) => g.dealer === d.name).reduce((s, g) => s + g.opportunity, 0)),
  })).sort((a, b) => b.opportunity - a.opportunity);
  return { gaps, byDealer, totalOpportunity: round2(gaps.reduce((s, g) => s + g.opportunity, 0)), gapCount: gaps.length };
}

/** C09 Omnichannel conflict. */
export function channelConflict({ mrp, onlineSellingPrice, dealerPrice, dealerMarginPct, distributorMarginPct, onlineSharePct = 20 }) {
  const m = Math.max(0, num(mrp));
  const online = Math.max(0, num(onlineSellingPrice));
  const dealer = Math.max(0, num(dealerPrice));
  const undercutPct = dealer > 0 ? round2(Math.max(0, (dealer - online) / dealer) * 100) : 0;
  const dealerMarginRs = dealer * clamp(num(dealerMarginPct), 0, 60) / 100;
  // If dealer must match online price, margin compresses:
  const matchedMarginRs = Math.max(0, online - (dealer - dealerMarginRs));
  const marginErosionPct = dealerMarginRs > 0 ? round2(Math.max(0, (dealerMarginRs - matchedMarginRs) / dealerMarginRs) * 100) : 0;
  const conflictIndex = clamp(round2(undercutPct * 2 + (num(onlineSharePct, 20) / 100) * undercutPct), 0, 100);
  return {
    undercutPct, dealerMarginRs: round2(dealerMarginRs), matchedMarginRs: round2(matchedMarginRs),
    marginErosionPct, conflictIndex,
    severity: conflictIndex >= 40 ? "high" : conflictIndex >= 20 ? "medium" : "low",
  };
}

/** C13 Export landed economics. Margins applied on selling price (mark-down chain). */
export function landedCost({ exWorks, freight, insurancePct = 1, dutyPct, clearance = 0, localFreight = 0, importerMarginPct = 0, distributorMarginPct = 0, retailerMarginPct = 0 }) {
  const cif = Math.max(0, num(exWorks)) + Math.max(0, num(freight)) + Math.max(0, num(exWorks)) * clamp(num(insurancePct, 1), 0, 20) / 100;
  const duty = cif * clamp(num(dutyPct), 0, 200) / 100;
  const landed = cif + duty + Math.max(0, num(clearance)) + Math.max(0, num(localFreight));
  // Each margin m on selling price: price = cost / (1 - m)
  let price = landed;
  const chain = [{ label: "Landed cost", value: round2(landed) }];
  [["Importer", importerMarginPct], ["Distributor", distributorMarginPct], ["Retailer", retailerMarginPct]].forEach(([label, m]) => {
    const frac = clamp(num(m), 0, 80) / 100;
    price = frac < 1 ? price / (1 - frac) : price;
    chain.push({ label: `${label} price (${num(m)}% margin)`, value: round2(price) });
  });
  return { cif: round2(cif), duty: round2(duty), landed: round2(landed), requiredShelfPrice: round2(price), multiple: landed > 0 ? round2(price / landed) : 0, chain };
}

/** C14 Sell-in vs sell-through. rows: [{sku, openingUnits, primaryIn, secondaryOut, weeks}]. */
export function sellThrough(rows) {
  const out = rows.map((r) => {
    const closing = Math.max(0, num(r.openingUnits)) + Math.max(0, num(r.primaryIn)) - Math.max(0, num(r.secondaryOut));
    const weeks = Math.max(0.25, num(r.weeks, 4.33));
    const weeklySecondary = num(r.secondaryOut) / weeks;
    const coverWeeks = weeklySecondary > 0 ? closing / weeklySecondary : closing > 0 ? 99 : 0;
    const sellThroughPct = (num(r.openingUnits) + num(r.primaryIn)) > 0 ? round2((num(r.secondaryOut) / (num(r.openingUnits) + num(r.primaryIn))) * 100) : 0;
    const flag = coverWeeks >= 8 ? "overstock" : coverWeeks <= 2 && weeklySecondary > 0 ? "stockout-risk" : sellThroughPct < 40 ? "slow" : "healthy";
    return { sku: r.sku, closingUnits: round2(closing), weeklySecondary: round2(weeklySecondary), coverWeeks: round2(Math.min(99, coverWeeks)), sellThroughPct, flag };
  });
  return { rows: out, overstocked: out.filter((r) => r.flag === "overstock").length, atRisk: out.filter((r) => r.flag === "stockout-risk").length };
}

/** C08 Territory coverage workload. */
export function territoryCoverage({ outlets, visitsPerMonth, visitMinutes = 30, travelMinutesPerDay = 90, workDaysPerMonth = 24, workHoursPerDay = 8, currentReps }) {
  const totalVisits = Math.max(0, num(outlets)) * Math.max(0, num(visitsPerMonth));
  const visitHours = (totalVisits * clamp(num(visitMinutes, 30), 5, 480)) / 60;
  const travelHoursPerRep = (num(workDaysPerMonth, 24) * clamp(num(travelMinutesPerDay, 90), 0, 480)) / 60;
  const capacityPerRep = num(workDaysPerMonth, 24) * num(workHoursPerDay, 8) - travelHoursPerRep;
  const requiredReps = capacityPerRep > 0 ? totalVisits * (num(visitMinutes, 30) / 60) / capacityPerRep : 0;
  const cur = Math.max(0, num(currentReps));
  return {
    totalVisitsPerMonth: round2(totalVisits), visitHoursRequired: round2(visitHours),
    capacityHoursPerRep: round2(Math.max(0, capacityPerRep)),
    requiredReps: round2(requiredReps), requiredRepsCeil: Math.ceil(requiredReps),
    gap: round2(cur - requiredReps),
    verdict: cur < requiredReps - 0.25 ? "under-covered" : cur > requiredReps + 1 ? "over-staffed" : "balanced",
  };
}

/** C01 / C10-style gate scoring. gates: [{label, score 0-5, hardStop:bool, threshold}] */
export function gateScore(gates) {
  const scored = gates.map((g) => ({ ...g, s: clamp(num(g.score), 0, 5) }));
  const failed = scored.filter((g) => g.hardStop && g.s < num(g.threshold, 3));
  const avg = scored.length ? scored.reduce((s, g) => s + g.s, 0) / scored.length : 0;
  return {
    failedGates: failed.map((g) => g.label),
    avgScore: round2(avg), scorePct: round2((avg / 5) * 100),
    verdict: failed.length ? "no-go" : avg >= 3.5 ? "go" : "conditional",
  };
}

/** S02 Funnel leakage. stages: [{name, count}] ordered top→bottom. */
export function funnelLeakage({ stages, avgDealValue }) {
  const adv = Math.max(0, num(avgDealValue));
  const rows = stages.map((s, i) => {
    const count = Math.max(0, num(s.count));
    const next = i < stages.length - 1 ? Math.max(0, num(stages[i + 1].count)) : null;
    const convPct = next !== null && count > 0 ? round2((next / count) * 100) : null;
    const lost = next !== null ? Math.max(0, count - next) : 0;
    return { name: s.name, count: round2(count), convPct, lost: round2(lost), lostValue: round2(lost * adv) };
  });
  const first = Math.max(0, num(stages[0]?.count));
  const last = Math.max(0, num(stages[stages.length - 1]?.count));
  // downstream(i): probability that a unit advancing past stage i ultimately reaches the end
  const downstream = (i) => {
    let p = 1;
    for (let j = i; j < stages.length - 1; j++) {
      const c = Math.max(0, num(stages[j].count));
      if (c <= 0) return 0;
      p *= Math.min(1, Math.max(0, num(stages[j + 1].count)) / c);
    }
    return p;
  };
  // value each stage's drop-outs at their real chance of having bought, not at full value
  rows.forEach((r, i) => { r.lostValue = round2(r.lost * downstream(i) * adv); });
  const weakest = rows.slice(0, -1).reduce((m, r) => (r.convPct !== null && (m === null || r.convPct < m.convPct) ? r : m), null);
  // what-if: +10% relative conversion at the weakest stage, propagated to the end of the funnel
  const whatIf = weakest && weakest.convPct !== null && weakest.convPct < 100
    ? (() => {
        const wi = rows.indexOf(weakest);
        const extraAtStage = weakest.count * Math.min(1, (weakest.convPct * 1.1) / 100) - (weakest.count * weakest.convPct) / 100;
        // extra units ENTER stage wi+1 — value them at that stage's chance of reaching the end
        const extraDeals = round2(extraAtStage * downstream(wi + 1));
        return { stage: weakest.name, extraDeals, extraValue: round2(extraDeals * adv) };
      })()
    : null;
  return {
    rows,
    endToEndPct: first > 0 ? round2((last / first) * 100) : 0,
    totalLostValue: round2(rows.reduce((s, r) => s + r.lostValue, 0)),
    weakestStage: weakest?.name || null,
    whatIf,
  };
}

/** S03 Pipeline hygiene. deals: [{name, value, probPct, lastActivityDays, evidence 0-2}]. */
export function pipelineHealth(deals) {
  const d = deals.map((x) => ({ ...x, value: Math.max(0, num(x.value)), prob: clamp(num(x.probPct), 0, 100) / 100, lastAct: Math.max(0, num(x.lastActivityDays)), ev: clamp(num(x.evidence), 0, 2) }));
  const total = d.reduce((s, x) => s + x.value, 0);
  const weighted = d.reduce((s, x) => s + x.value * x.prob, 0);
  const trusted = d.filter((x) => x.ev >= 1 && x.lastAct <= 21).reduce((s, x) => s + x.value * x.prob, 0);
  const stale = d.filter((x) => x.lastAct > 21);
  const unevidenced = d.filter((x) => x.ev === 0);
  const activeShare = d.length ? d.filter((x) => x.lastAct <= 14).length / d.length : 0;
  const evidenceShare = d.length ? d.filter((x) => x.ev >= 1).length / d.length : 0;
  const score = Math.round(100 * (0.45 * activeShare + 0.45 * evidenceShare + 0.1 * (stale.length === 0 ? 1 : 1 - stale.length / d.length)));
  return {
    totalValue: round2(total), weightedValue: round2(weighted), trustworthyValue: round2(trusted),
    staleCount: stale.length, staleValue: round2(stale.reduce((s, x) => s + x.value, 0)),
    unevidencedCount: unevidenced.length,
    hygieneScore: d.length ? score : 0,
    grade: score >= 75 ? "forecast-ready" : score >= 50 ? "directional" : "wishlist",
    cleanup: [...stale.map((x) => `Requalify or close "${x.name}" — ${x.lastAct} days silent`), ...unevidenced.map((x) => `Get buyer-verified evidence for "${x.name}"`)],
  };
}

/** S05 Incentive plan simulator. */
export function incentiveSim({ fixedAnnual, variableTarget, thresholdPct = 70, acceleratorPct = 0, acceleratorFromPct = 100, capMultiple = 0 }) {
  const fixed = Math.max(0, num(fixedAnnual));
  const vt = Math.max(0, num(variableTarget));
  const th = clamp(num(thresholdPct, 70), 0, 100);
  const accFrom = clamp(num(acceleratorFromPct, 100), 100, 200);
  const accPct = clamp(num(acceleratorPct), 0, 100);
  const cap = Math.max(0, num(capMultiple));
  const payout = (ach) => {
    if (ach < th) return 0;
    let v = vt * Math.min(ach, accFrom) / 100;
    if (ach > accFrom) v += vt * ((ach - accFrom) / 100) * (1 + accPct / 100);
    if (cap > 0) v = Math.min(v, vt * cap);
    return v;
  };
  const curve = [60, 70, 80, 90, 100, 110, 120, 130, 140].map((a) => ({ achievementPct: a, variable: round2(payout(a)), total: round2(fixed + payout(a)) }));
  const flags = [];
  if (th === 0) flags.push("Pays from the first rupee — expect sandbagged targets and no performance pressure.");
  if (accPct >= 50) flags.push("Steep accelerator: watch for deal-pulling into high-accelerator quarters.");
  if (cap > 0 && cap <= 1.2) flags.push("Tight cap: top performers stop selling after hitting it.");
  // cliff: flag the LARGEST payout jump anywhere on the curve, not just at 80→90
  let maxJump = 0, jumpAt = null;
  for (let i = 1; i < curve.length; i++) {
    const j = curve[i].variable - curve[i - 1].variable;
    if (j > maxJump) { maxJump = j; jumpAt = curve[i].achievementPct; }
  }
  if (vt > 0 && maxJump / vt > 0.15) flags.push(`Cliff near ${jumpAt}% achievement — payout jumps ${inr(maxJump)} for a small achievement change; expect disputes and timing games.`);
  return {
    curve,
    payoutAt100: round2(payout(100)),
    costAt100Pct: fixed + vt > 0 ? round2((payout(100) / (fixed + vt)) * 100) : 0,
    maxPayout: round2(Math.max(...curve.map((c) => c.variable))),
    flags,
  };
}

/** S12 Sales capacity / headcount. */
export function salesCapacity({ revenueTarget, avgDealSize, winRatePct = 25, meetingsPerWeekPerRep = 15, meetingsToClose = 6, workingWeeks = 46, currentReps = 0 }) {
  const dealsNeeded = num(avgDealSize) > 0 ? num(revenueTarget) / num(avgDealSize) : 0;
  const wr = clamp(num(winRatePct, 25), 1, 100) / 100;
  const oppsNeeded = dealsNeeded / wr;
  const meetingsNeeded = oppsNeeded * Math.max(1, num(meetingsToClose, 6));
  const oppsPerRepYear = (num(meetingsPerWeekPerRep, 15) * num(workingWeeks, 46)) / Math.max(1, num(meetingsToClose, 6));
  const repsRequired = oppsPerRepYear > 0 ? oppsNeeded / oppsPerRepYear : 0;
  return {
    dealsNeeded: round2(dealsNeeded), oppsNeeded: round2(oppsNeeded), meetingsNeeded: round2(meetingsNeeded),
    oppsPerRepYear: round2(oppsPerRepYear),
    repsRequired: round2(repsRequired), repsRequiredCeil: Math.ceil(repsRequired),
    gap: round2(num(currentReps) - repsRequired),
    verdict: num(currentReps) < repsRequired - 0.25 ? "under-resourced" : num(currentReps) > repsRequired + 1 ? "over-resourced" : "right-sized",
  };
}

/** B10 Campaign ROI forecaster. */
export function campaignRoi({ campaignCost, avgOrderValue, contributionPct, baselineWeeklyOrders = 0, expectedUpliftPct = 0, durationWeeks = 4 }) {
  const cost = Math.max(0, num(campaignCost));
  const cm = num(avgOrderValue) * clamp(num(contributionPct), 0, 100) / 100;
  const incrementalOrders = Math.max(0, num(baselineWeeklyOrders)) * (clamp(num(expectedUpliftPct), 0, 500) / 100) * Math.max(0, num(durationWeeks, 4));
  const incrementalContribution = incrementalOrders * cm;
  const breakEvenOrders = cm > 0 ? cost / cm : null;
  const roi = cost > 0 ? ((incrementalContribution - cost) / cost) * 100 : null;
  return {
    contributionPerOrder: round2(cm),
    incrementalOrders: round2(incrementalOrders),
    incrementalContribution: round2(incrementalContribution),
    breakEvenOrders: breakEvenOrders === null ? null : Math.ceil(breakEvenOrders),
    breakEvenUpliftPct: breakEvenOrders !== null && num(baselineWeeklyOrders) > 0 && num(durationWeeks) > 0
      ? round2((breakEvenOrders / (num(baselineWeeklyOrders) * num(durationWeeks, 4))) * 100) : null,
    roiPct: roi === null ? null : round2(roi),
    profitable: roi !== null && roi > 0,
  };
}

/** B13 Influencer ROI. */
export function influencerRoi({ fee, deliverableCost = 0, reach, engagementPct = 3, clickPct = 20, conversionPct = 2, avgOrderValue, contributionPct }) {
  const cost = Math.max(0, num(fee)) + Math.max(0, num(deliverableCost));
  const engaged = Math.max(0, num(reach)) * clamp(num(engagementPct, 3), 0, 100) / 100;
  const visits = engaged * clamp(num(clickPct, 20), 0, 100) / 100;
  const orders = visits * clamp(num(conversionPct, 2), 0, 100) / 100;
  const cm = num(avgOrderValue) * clamp(num(contributionPct), 0, 100) / 100;
  const contribution = orders * cm;
  const breakEvenOrders = cm > 0 ? cost / cm : null;
  return {
    totalCost: round2(cost), engaged: round2(engaged), visits: round2(visits), orders: round2(orders),
    contribution: round2(contribution),
    breakEvenOrders: breakEvenOrders === null ? null : Math.ceil(breakEvenOrders),
    roiPct: cost > 0 ? round2(((contribution - cost) / cost) * 100) : null,
    costPerOrder: orders > 0 ? round2(cost / orders) : null,
    profitable: contribution > cost,
  };
}

/** B09 Budget allocation scenario. Assumptions are user-set, labelled, deterministic. */
export function budgetAllocation({ budget, brandPct, performancePct, retentionPct, brandMultiplier = 1.5, perfRoas = 3, retentionRoas = 5 }) {
  const b = Math.max(0, num(budget));
  const pb = clamp(num(brandPct), 0, 100) / 100, pp = clamp(num(performancePct), 0, 100) / 100, pr = clamp(num(retentionPct), 0, 100) / 100;
  const total = pb + pp + pr;
  const norm = total > 0 ? 1 / total : 0;
  const brand = b * pb * norm, perf = b * pp * norm, ret = b * pr * norm;
  // Horizon honesty: brand returns accrue over 12m at user-set multiplier; perf/retention at user-set ROAS.
  const returns = { brand: brand * num(brandMultiplier, 1.5), performance: perf * num(perfRoas, 3), retention: ret * num(retentionRoas, 5) };
  const totalReturn = returns.brand + returns.performance + returns.retention;
  const warnings = [];
  if (pb * norm > 0.7) warnings.push("Over 70% brand: slow feedback loop — only viable with strong measurement.");
  if (pp * norm > 0.7) warnings.push("Over 70% performance: short-term harvesting erodes future demand.");
  if (pb * norm === 0) warnings.push("Zero brand spend: performance channels eventually run out of demand to harvest.");
  if (pr * norm === 0) warnings.push("Zero retention spend: reacquiring churned customers costs multiples of keeping them.");
  return {
    split: { brand: round2(brand), performance: round2(perf), retention: round2(ret) },
    projectedReturn: { brand: round2(returns.brand), performance: round2(returns.performance), retention: round2(returns.retention) },
    totalReturn: round2(totalReturn),
    blendedMultiple: b > 0 ? round2(totalReturn / b) : 0,
    warnings,
  };
}

/** D04 D2C vs marketplace per-order comparison with completeness flags. */
export function channelCompare({ price, productCost, d2c, mkt }) {
  const p = Math.max(0, num(price)), c = Math.max(0, num(productCost));
  const perOrder = (ch) => {
    const fees = p * clamp(num(ch.feePct), 0, 90) / 100 + num(ch.flatFee);
    const payment = p * clamp(num(ch.paymentPct), 0, 10) / 100;
    const returnsCost = p * clamp(num(ch.returnsPct), 0, 80) / 100 * 0.35; // 35% of order value lost per return event (disclosed assumption)
    const total = c + fees + payment + num(ch.logistics) + returnsCost + num(ch.adPerOrder);
    return { contribution: round2(p - total), contributionPct: p > 0 ? round2(((p - total) / p) * 100) : 0, totalCost: round2(total) };
  };
  const d = perOrder(d2c || {}), m = perOrder(mkt || {});
  const flags = [];
  if (mkt && num(mkt.feePct) === 0) flags.push("Marketplace commission at 0% — almost never true. Check referral + closing fees.");
  if (d2c && num(d2c.logistics) === 0) flags.push("D2C shipping at ₹0 — are you hiding a subsidy customers now expect?");
  if (d2c && num(d2c.adPerOrder) === 0 && mkt && num(mkt.adPerOrder) > 0) flags.push("D2C has no acquisition cost loaded — unlike marketplaces, D2C traffic is never free.");
  return { d2c: d, marketplace: m, delta: round2(d.contribution - m.contribution), flags };
}

/** D06 Cohort retention from orders: [{customerId, dateISO}]. Returns M0..M5 repeat rates. */
export function cohortRetention(orders) {
  const valid = orders.filter((o) => o.customerId && o.dateISO && !isNaN(Date.parse(o.dateISO)));
  if (!valid.length) return { cohorts: [], months: [], error: "no-valid-rows", parsed: 0, total: orders.length };
  const monthOf = (d) => { const dt = new Date(d); return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`; };
  const firstSeen = {};
  valid.forEach((o) => {
    const m = monthOf(o.dateISO);
    if (!firstSeen[o.customerId] || m < firstSeen[o.customerId]) firstSeen[o.customerId] = m;
  });
  const cohortMonths = [...new Set(Object.values(firstSeen))].sort();
  const activity = {}; // cohort -> month -> Set(customerId)
  valid.forEach((o) => {
    const c = firstSeen[o.customerId];
    const m = monthOf(o.dateISO);
    activity[c] = activity[c] || {};
    activity[c][m] = activity[c][m] || new Set();
    activity[c][m].add(o.customerId);
  });
  // second-order timing
  const byCust = {};
  valid.forEach((o) => { (byCust[o.customerId] = byCust[o.customerId] || []).push(Date.parse(o.dateISO)); });
  const gaps = Object.values(byCust).filter((d) => d.length > 1).map((d) => { d.sort(); return (d[1] - d[0]) / 86400000; }).sort((a, b) => a - b);
  const medianSecondOrderDays = gaps.length ? round2(gaps[Math.floor(gaps.length / 2)]) : null;
  const cohorts = cohortMonths.slice(-6).map((cm) => {
    const size = Object.values(firstSeen).filter((m) => m === cm).length;
    const rates = [];
    for (let off = 0; off <= 5; off++) {
      const [y, mo] = cm.split("-").map(Number);
      const target = `${new Date(Date.UTC(y, mo - 1 + off, 1)).getUTCFullYear()}-${String(new Date(Date.UTC(y, mo - 1 + off, 1)).getUTCMonth() + 1).padStart(2, "0")}`;
      const nowM = monthOf(new Date().toISOString());
      if (target > nowM) { rates.push(null); continue; }
      const active = activity[cm]?.[target]?.size || 0;
      rates.push(size > 0 ? round2((active / size) * 100) : 0);
    }
    return { month: cm, size, rates };
  });
  return { cohorts, medianSecondOrderDays, parsed: valid.length, total: orders.length, customers: Object.keys(firstSeen).length };
}

/** D12 Pricing war: hold / match / bundle scenarios. Elasticity: units change per % price gap. */
export function pricingWar({ ourPrice, variableCost, weeklyUnits, compDropPct = 15, holdLossSensitivity = 0.8, bundleCostPerUnit = 0, bundleRetentionFactor = 0.5 }) {
  const p = num(ourPrice), c = num(variableCost), u = Math.max(0, num(weeklyUnits));
  const drop = clamp(num(compDropPct, 15), 0, 90);
  const sens = Math.max(0, num(holdLossSensitivity, 0.8)); // % units lost per 1% price disadvantage
  const base = u * (p - c);
  const holdUnits = u * Math.max(0, 1 - (sens * drop) / 100);
  const hold = { label: "Hold price", units: round2(holdUnits), contribution: round2(holdUnits * (p - c)), vsBase: round2(holdUnits * (p - c) - base) };
  const matchPrice = p * (1 - drop / 100);
  const match = { label: "Match the cut", units: round2(u), contribution: round2(u * (matchPrice - c)), vsBase: round2(u * (matchPrice - c) - base), newPrice: round2(matchPrice) };
  const bundleUnits = u * Math.max(0, 1 - (sens * drop * clamp(num(bundleRetentionFactor, 0.5), 0, 1)) / 100);
  const bundle = { label: "Hold + bundle value", units: round2(bundleUnits), contribution: round2(bundleUnits * (p - c - num(bundleCostPerUnit))), vsBase: round2(bundleUnits * (p - c - num(bundleCostPerUnit)) - base) };
  const scenarios = [hold, match, bundle];
  const best = scenarios.reduce((mx, s) => (s.contribution > mx.contribution ? s : mx), scenarios[0]);
  return { base: round2(base), scenarios, best: best.label, matchMarginNegative: matchPrice - c <= 0 };
}

/** I01 Dealer advocacy: whose economics win the dealer's push. */
export function dealerAdvocacy({ ours, rival }) {
  const side = (s) => {
    const margin = num(s.price) * clamp(num(s.marginPct), 0, 60) / 100;
    const perUnit = margin + num(s.incentivePerUnit);
    const monthly = perUnit * Math.max(0, num(s.monthlyUnits));
    const creditBenefit = num(s.price) * 0.12 * (clamp(num(s.creditDays), 0, 180) / 365) * Math.max(0, num(s.monthlyUnits)); // supplier credit saves the dealer ~12% p.a. of financing
    return { perUnit: round2(perUnit), monthly: round2(monthly), netMonthly: round2(monthly + creditBenefit) };
  };
  const o = side(ours || {}), r = side(rival || {});
  return {
    ours: o, rival: r,
    gapMonthly: round2(o.netMonthly - r.netMonthly),
    verdict: o.netMonthly > r.netMonthly * 1.1 ? "winning" : o.netMonthly >= r.netMonthly ? "competitive" : "losing",
  };
}

/** I03 Market basket analysis. rows: [{orderId, item}]. Returns top pairs with support/confidence/lift. */
export function basketAnalysis(rows, { minSupportPct = 2 } = {}) {
  const baskets = {};
  rows.forEach((r) => {
    if (!r.orderId || !r.item) return;
    (baskets[r.orderId] = baskets[r.orderId] || new Set()).add(r.item.trim());
  });
  const n = Object.keys(baskets).length;
  if (n < 2) return { pairs: [], baskets: n, error: "need-more-baskets" };
  const itemCount = {}, pairCount = {};
  Object.values(baskets).forEach((set) => {
    const items = [...set];
    items.forEach((i) => { itemCount[i] = (itemCount[i] || 0) + 1; });
    for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) {
      const key = [items[i], items[j]].sort().join("|||");
      pairCount[key] = (pairCount[key] || 0) + 1;
    }
  });
  const pairs = Object.entries(pairCount).map(([key, count]) => {
    const [a, b] = key.split("|||");
    const support = count / n;
    const confAtoB = count / (itemCount[a] || 1);
    const confBtoA = count / (itemCount[b] || 1);
    const expected = ((itemCount[a] || 0) / n) * ((itemCount[b] || 0) / n);
    const lift = expected > 0 ? support / expected : 0;
    return { a, b, count, supportPct: round2(support * 100), confidencePct: round2(Math.max(confAtoB, confBtoA) * 100), lift: round2(lift) };
  }).filter((p) => p.supportPct >= minSupportPct).sort((x, y) => y.lift - x.lift);
  return { pairs: pairs.slice(0, 15), baskets: n, uniqueItems: Object.keys(itemCount).length };
}

/** I05 Enrolment funnel economics. */
export function enrolmentFunnel({ monthlyEnquiries, counsellingPct, applicationPct, enrolmentPct, avgFee, marketingSpendMonthly, counsellorMonthlyCost = 0 }) {
  const enq = Math.max(0, num(monthlyEnquiries));
  const c = clamp(num(counsellingPct), 0, 100) / 100;
  const a = clamp(num(applicationPct), 0, 100) / 100;
  const e = clamp(num(enrolmentPct), 0, 100) / 100;
  const counselled = enq * c, applied = counselled * a, enrolled = applied * e;
  const spend = Math.max(0, num(marketingSpendMonthly)) + Math.max(0, num(counsellorMonthlyCost));
  const cacPerEnrolment = enrolled > 0 ? spend / enrolled : null;
  const revenue = enrolled * Math.max(0, num(avgFee));
  const weakest = [
    { stage: "enquiry→counselling", conv: c }, { stage: "counselling→application", conv: a }, { stage: "application→enrolment", conv: e },
  ].sort((x, y) => x.conv - y.conv)[0];
  return {
    counselled: round2(counselled), applied: round2(applied), enrolled: round2(enrolled),
    endToEndPct: enq > 0 ? round2((enrolled / enq) * 100) : 0,
    cacPerEnrolment: cacPerEnrolment === null ? null : round2(cacPerEnrolment),
    revenue: round2(revenue),
    roiPct: spend > 0 ? round2(((revenue - spend) / spend) * 100) : null,
    weakestStage: weakest.stage,
  };
}

/** I07 Real-estate sell-out planner. */
export function selloutPlan({ totalUnits, soldUnits, avgPrice, monthsLeft, siteVisitToBookingPct = 8, leadToVisitPct = 25 }) {
  const remaining = Math.max(0, num(totalUnits) - num(soldUnits));
  const months = Math.max(1, num(monthsLeft, 6));
  const bookingsPerMonth = remaining / months;
  const s2b = clamp(num(siteVisitToBookingPct, 8), 0.5, 100) / 100;
  const l2v = clamp(num(leadToVisitPct, 25), 1, 100) / 100;
  const visitsPerMonth = bookingsPerMonth / s2b;
  const leadsPerMonth = visitsPerMonth / l2v;
  const pipelineValue = remaining * Math.max(0, num(avgPrice));
  return {
    remainingUnits: round2(remaining),
    bookingsPerMonth: round2(bookingsPerMonth),
    visitsPerMonth: Math.ceil(visitsPerMonth),
    leadsPerMonth: Math.ceil(leadsPerMonth),
    pipelineValue: round2(pipelineValue),
    weeklyVisits: Math.ceil(visitsPerMonth / 4.33),
  };
}

/** I04 Capacity-to-revenue map. */
export function capacityMap({ monthlyCapacityUnits, utilizationPct, candidates }) {
  const idle = Math.max(0, num(monthlyCapacityUnits)) * (1 - clamp(num(utilizationPct), 0, 100) / 100);
  const options = (candidates || []).map((c) => {
    const contribution = Math.max(0, num(c.pricePerUnit) - num(c.variableCostPerUnit));
    const absorbable = Math.min(idle, Math.max(0, num(c.demandUnits)));
    return { segment: c.segment, contributionPerUnit: round2(contribution), absorbableUnits: round2(absorbable), monthlyContribution: round2(contribution * absorbable) };
  }).filter((o) => o.contributionPerUnit > 0).sort((a, b) => b.monthlyContribution - a.monthlyContribution);
  let remaining = idle;
  const plan = options.map((o) => {
    const take = Math.min(remaining, o.absorbableUnits);
    remaining -= take;
    return { ...o, allocatedUnits: round2(take) };
  });
  return {
    idleUnits: round2(idle),
    plan,
    totalAbsorbableContribution: round2(plan.reduce((s, p) => s + p.contributionPerUnit * p.allocatedUnits, 0)),
    unallocatedIdle: round2(remaining),
  };
}
